/**
 * PostHog client wrapper (NB-F8). Lazy-initializes the SDK only when
 * `NEXT_PUBLIC_POSTHOG_KEY` is set AND the user has granted analytics
 * consent (per CookieBanner). Until both conditions hold, every call
 * here is a no-op: there is no network traffic, no autocapture, no
 * cookies set.
 *
 * The 8-week acceptance criteria (owner-mandated v61) requires:
 *   - ≥50% signup → first-company activation funnel
 *   - ≥80% first-company → first-briefing-open funnel
 *
 * Call sites:
 *   - `captureEvent('signup_completed', { plan })` after successful auth
 *   - `captureEvent('first_company_added', { company_id })` on watchlist insert
 *   - `captureEvent('first_briefing_opened', { briefing_id })` on briefing render
 *   - `identifyUser(userId, traits)` once the auth state resolves
 *
 * `init()` is called once from `<ObservabilityBoot />` on app mount.
 */
'use client'

import { readConsent } from '@/components/CookieBanner'

type PostHogClient = typeof import('posthog-js').default

let posthogClient: PostHogClient | null = null
let initAttempted = false

function getKey(): string | null {
  if (typeof process === 'undefined') return null
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY?.trim()
  return key && key.length > 0 ? key : null
}

function getHost(): string {
  // Defaults to PostHog Cloud US. Override via NEXT_PUBLIC_POSTHOG_HOST
  // for EU / self-hosted (e.g. https://eu.i.posthog.com).
  if (typeof process === 'undefined') return 'https://us.i.posthog.com'
  return process.env.NEXT_PUBLIC_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com'
}

export async function init(): Promise<void> {
  if (initAttempted) return
  initAttempted = true
  if (typeof window === 'undefined') return
  const key = getKey()
  if (!key) return
  const consent = readConsent()
  if (consent !== 'all' && consent !== 'analytics') return
  try {
    const mod = await import('posthog-js')
    const ph = mod.default
    ph.init(key, {
      api_host: getHost(),
      capture_pageview: 'history_change',
      autocapture: true,
      disable_session_recording: true,
      persistence: 'localStorage+cookie',
      cross_subdomain_cookie: true,
      // Respect Do-Not-Track / Global Privacy Control by default.
      respect_dnt: true,
      loaded: (instance) => {
        posthogClient = instance as unknown as PostHogClient
      },
    })
  } catch {
    // SDK load failed (e.g. CSP block, offline at boot). Silent.
  }
}

export function captureEvent(
  name: string,
  properties: Record<string, unknown> = {},
): void {
  // Use the cached client when present; otherwise no-op. We deliberately
  // do NOT queue events pre-init; PostHog autocapture handles the
  // common case once init() completes.
  if (!posthogClient) return
  try {
    ;(posthogClient as unknown as { capture: (n: string, p?: Record<string, unknown>) => void }).capture(name, properties)
  } catch {
    // No-op
  }
}

export function identifyUser(
  userId: string | number,
  traits: Record<string, unknown> = {},
): void {
  if (!posthogClient) return
  try {
    ;(posthogClient as unknown as { identify: (id: string, p?: Record<string, unknown>) => void }).identify(
      String(userId),
      traits,
    )
  } catch {
    // No-op
  }
}

export function resetUser(): void {
  if (!posthogClient) return
  try {
    ;(posthogClient as unknown as { reset: () => void }).reset()
  } catch {
    // No-op
  }
}

/**
 * GDPR-critical (NB-F8): when the user flips cookie consent from
 * 'all' / 'analytics' to 'essential', we must immediately stop
 * capturing events + clear the device identifier. ObservabilityBoot
 * calls this from the ps:consent listener when the new consent state
 * disallows analytics.
 *
 * Safe to call when the SDK isn't initialized (returns silently).
 * Idempotent: calling twice is a no-op the second time.
 */
export function optOut(): void {
  if (!posthogClient) return
  try {
    const client = posthogClient as unknown as {
      opt_out_capturing: () => void
      reset: () => void
    }
    client.opt_out_capturing()
    client.reset()
  } catch {
    // No-op
  }
}
